import { useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";

import { MEDIUM_LIVED_CACHE } from "@/lib/constants/cache";
import type { UserPermissions, UUID } from "@/core/types";

import { getUserPermissions } from "../queryFunctions/getUserPermissions";
import { permissionsKeys } from "../keyFactory";

type UseUserPermissionsProps<TData = UserPermissions> = {
  enabled?: boolean;
  select?: (data: UserPermissions) => TData;
  userId: undefined | UUID;
};

type UserPermissionsQueryOptions = UseQueryOptions<
  UserPermissions,
  unknown,
  UserPermissions,
  ReturnType<typeof permissionsKeys.userPermissions>
>;

function optionFactory(userId: undefined | UUID, enabled = true) {
  return {
    enabled: enabled && !!userId,
    queryFn: () => getUserPermissions(userId as UUID),
    queryKey: permissionsKeys.userPermissions(userId as UUID),
    staleTime: MEDIUM_LIVED_CACHE,
  } satisfies UserPermissionsQueryOptions;
}

export function useUserPermissions<TData = UserPermissions>({
  enabled = true,
  select,
  userId,
}: UseUserPermissionsProps<TData>) {
  return useQuery({ ...optionFactory(userId, enabled), select });
}
